// src/pages/products/BulkProductImportPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  Download,
  Loader2,
  RefreshCw,
  Send,
  Columns,
} from 'lucide-react';
import { getBulkProductSettings, saveBulkProductSettings } from '../../services/api/bulkProductSettings';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import useBulkProductImport from '../../hooks/useBulkProductImport';
import ShopMultiSelector from './components/bulk/ShopMultiSelector';
import WarehouseSelector from './components/bulk/WarehouseSelector';
import FileUploadZone from '../../components/common/bulk/FileUploadZone';
import ProductDataPreviewTable from './components/bulk/ProductDataPreviewTable';
import ValidationResults from '../../components/common/bulk/ValidationResults';
import ProgressIndicator from '../../components/common/bulk/ProgressIndicator';
import ResultsSummary from '../../components/common/bulk/ResultsSummary';
import ColumnMappingModal from '../../components/common/bulk/ColumnMappingModal';
import styles from './BulkProductImportPage.module.css';

// The canonical column names used in our standard bulk-product template
const MAPPABLE_PRODUCT_FIELDS = [
  { key: 'SKU',           label: 'SKU',           required: true  },
  { key: 'Name',          label: 'Name',          required: true  },
  { key: 'Printable_Name', label: 'Printable Name', required: false },
  { key: 'Description',   label: 'Description',   required: false },
  { key: 'Cost_Price',    label: 'Cost Price',    required: false },
  { key: 'Barcode',       label: 'Barcode',       required: false },
  { key: 'Weight',        label: 'Weight',        required: false },
  { key: 'Category',      label: 'Category',      required: false },
  { key: 'Brand',         label: 'Brand',         required: false },
  { key: 'Price',         label: 'Price',         required: true  },
  { key: 'Opening_Stock', label: 'Opening Stock', required: false },
  { key: 'Visibility',    label: 'Visibility',    required: false },
];

const BulkProductImportPage = () => {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();

  const [showMappingModal, setShowMappingModal] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);

  const {
    selectedShops,
    selectedWarehouse,
    uploadedFile,
    parsedData,
    validationResults,
    submissionResults,
    isProcessing,
    processingStep,
    progress,
    columnMappings,

    setSelectedShops,
    setSelectedWarehouse,
    setColumnMappings,
    handleFileUpload,
    handleValidation,
    handleSubmit,
    handleRetryValidation,
    removeInvalidRow,
    updateInvalidRow,
    clearAll,
    downloadTemplate,
  } = useBulkProductImport();

  // ── Load saved column mappings on mount ───────────────────────────────────
  useEffect(() => {
    const loadMappings = async () => {
      try {
        const settings = await getBulkProductSettings();
        if (settings.columnMappings) setColumnMappings(settings.columnMappings);
      } catch (err) {
        console.error('Failed to load column mappings:', err);
      }
    };
    loadMappings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Step helper ────────────────────────────────────────────────────────────
  const getStepStatus = (step) => {
    const steps        = ['select', 'upload', 'validate', 'submit'];
    const currentIndex = steps.indexOf(processingStep);
    const stepIndex    = steps.indexOf(step);
    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'active';
    return 'pending';
  };

  const isConfigLocked = parsedData.length > 0;

  // ── Template ───────────────────────────────────────────────────────────────
  const handleDownloadTemplate = async () => {
    setIsDownloadingTemplate(true);
    try {
      await downloadTemplate();
      success('Template downloaded successfully!');
    } catch (err) {
      showError(err.message || 'Failed to generate template');
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  // ── Error-wrapped handlers ─────────────────────────────────────────────────
  const handleFileUploadWithError = async (file) => {
    try { await handleFileUpload(file); }
    catch (err) { showError(err.message || 'Failed to parse the uploaded file.'); }
  };

  const handleValidationWithError = async () => {
    try { await handleValidation(); }
    catch (err) { showError(err.message || 'Validation failed. Please try again.'); }
  };

  const handleRetryValidationWithError = async () => {
    try { await handleRetryValidation(); }
    catch (err) { showError(err.message || 'Retry validation failed.'); }
  };

  const handleSubmitWithError = async () => {
    try { await handleSubmit(); }
    catch (err) { showError(err.message || 'Product import failed.'); }
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const activeMappingCount = Object.values(columnMappings).filter(v => Array.isArray(v) ? v.length > 0 : v?.trim()).length;

  const canValidate = selectedShops.length > 0 && parsedData.length > 0 && !isProcessing;
  const canSubmit   =
    validationResults &&
    validationResults.summary?.validCount > 0 &&
    validationResults.results?.every(r => r.status === 'valid') &&
    !isProcessing;

  return (
    <div className={styles.bulkProductPage}>
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <FileSpreadsheet className={styles.headerIcon} size={24} />
            <div className={styles.titleContent}>
              <h1>{t('products.bulkImportProductsTitle')}</h1>
              <p>{t('products.bulkImportProductsDesc')}</p>
            </div>
          </div>
          <div className={styles.headerActions}>
            <button
              onClick={() => setShowMappingModal(true)}
              className={`${styles.mappingButton} ${activeMappingCount > 0 ? styles.mappingButtonActive : ''}`}
              title="Configure column mappings for custom Excel headers"
            >
              <Columns size={16} />
              {t('products.columnMapping')}
              {activeMappingCount > 0 && (
                <span className={styles.mappingBadge}>{activeMappingCount}</span>
              )}
            </button>
            <button
              onClick={handleDownloadTemplate}
              className={styles.downloadButton}
              title={t('products.downloadTemplate')}
              disabled={isDownloadingTemplate}
            >
              {isDownloadingTemplate
                ? <Loader2 size={18} className={styles.spinning} />
                : <Download size={18} />}
              {t('products.downloadTemplate')}
            </button>
            <button onClick={() => navigate('/products')} className={styles.backButton}>
              <ArrowLeft size={18} />
              {t('products.backToProducts')}
            </button>
          </div>
        </div>
      </div>

      {/* ── Progress Steps ───────────────────────────────────────────────────── */}
      <div className={styles.progressSteps}>
        {['select', 'upload', 'validate', 'submit'].map((step, i, arr) => (
          <React.Fragment key={step}>
            <div className={`${styles.step} ${styles[getStepStatus(step)]}`}>
              <div className={styles.stepNumber}>{i + 1}</div>
              <div className={styles.stepLabel}>
                {step === 'select'   && t('products.selectShopsStep')}
                {step === 'upload'   && t('products.uploadFileStep')}
                {step === 'validate' && t('products.validateProducts')}
                {step === 'submit'   && t('products.submitProducts')}
              </div>
            </div>
            {i < arr.length - 1 && <div className={styles.stepConnector} />}
          </React.Fragment>
        ))}
      </div>

      <div className={styles.pageContent}>
        {/* ── Row 1: Shops (50%) + Warehouse (50%) ─────────────────────── */}
        {!submissionResults && (
          <div className={styles.configRow}>
            <div className={styles.configRowShop}>
              <ShopMultiSelector
                selectedShops={selectedShops}
                onShopsChange={setSelectedShops}
                disabled={isConfigLocked}
              />
            </div>
            <div className={styles.configRowWarehouse}>
              <WarehouseSelector
                selectedWarehouse={selectedWarehouse}
                onWarehouseChange={setSelectedWarehouse}
                disabled={isConfigLocked}
              />
            </div>
          </div>
        )}

        {/* File Upload */}
        {selectedShops.length > 0 && !submissionResults && (
          <FileUploadZone
            uploadedFile={uploadedFile}
            onFileUpload={handleFileUploadWithError}
            disabled={isProcessing}
          />
        )}

        {/* Data Preview */}
        {parsedData.length > 0 && !submissionResults && (
          <ProductDataPreviewTable
            data={parsedData}
            validationResults={validationResults}
            onRemoveRow={removeInvalidRow}
            onUpdateRow={updateInvalidRow}
          />
        )}

        {/* Validation Results */}
        {validationResults && !submissionResults && (
          <ValidationResults
            results={validationResults}
            onRetry={handleRetryValidationWithError}
            isProcessing={isProcessing}
            title={t('products.validationResults')}
            validLabel={t('products.validRows')}
            invalidLabel={t('products.invalidRows')}
            totalLabel={t('products.totalRows')}
          />
        )}

        {/* Processing Indicator */}
        {isProcessing && (
          <ProgressIndicator step={processingStep} progress={progress} entityLabel="product" />
        )}

        {/* Results Summary */}
        {submissionResults && (
          <ResultsSummary
            results={submissionResults}
            onCreateMore={clearAll}
            onViewResults={() => navigate('/products')}
            title={t('products.importComplete')}
            successLabel={t('products.successfullyImported')}
            failedLabel="Failed"
            totalLabel={t('products.totalSubmitted')}
            viewResultsLabel={t('products.viewProducts')}
            createMoreLabel={t('products.importMoreProducts')}
            failedListTitle={t('products.failedRows')}
          />
        )}

        {/* ── Action Buttons ─────────────────────────────────────────────── */}
        {parsedData.length > 0 && !submissionResults && (
          <div className={styles.actionButtons}>
            <button onClick={clearAll} className={styles.clearButton} disabled={isProcessing}>
              <XCircle size={18} />
              {t('products.clearAll')}
            </button>

            {!validationResults && (
              <button onClick={handleValidationWithError} className={styles.validateButton} disabled={!canValidate}>
                {isProcessing
                  ? <Loader2 size={18} className={styles.spinning} />
                  : <CheckCircle size={18} />}
                {t('products.validateProducts')}
              </button>
            )}

            {validationResults?.results?.some(r => r.status === 'failed') && (
              <button onClick={handleRetryValidationWithError} className={styles.retryButton} disabled={isProcessing}>
                <RefreshCw size={18} />
                {t('products.retryValidation')}
              </button>
            )}

            {canSubmit && (
              <button onClick={handleSubmitWithError} className={styles.submitButton} disabled={!canSubmit}>
                {isProcessing
                  ? <Loader2 size={18} className={styles.spinning} />
                  : <Send size={18} />}
                {t('products.submitValidProducts', { count: validationResults.summary.validCount })}
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Modals ──────────────────────────────────────────────────────────── */}
      {showMappingModal && (
        <ColumnMappingModal
          onClose={() => setShowMappingModal(false)}
          onSaved={(newMappings) => {
            setColumnMappings(newMappings);
            success('Column mappings saved!');
          }}
          fields={MAPPABLE_PRODUCT_FIELDS}
          getSettings={getBulkProductSettings}
          saveSettings={saveBulkProductSettings}
          title={t('products.columnMapping')}
          infoText="Map your Excel column headers to the standard product fields. Add multiple aliases per field — the first matching column in your file will be used. Leave empty to use the default column name."
        />
      )}
    </div>
  );
};

export default BulkProductImportPage;
